/* *** Fevre River Database *** */
/* (C) 2026 - Joti Software, LLC */

// Parris Review List View (Overview of Parris Review Items) //

import React, { Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import { withRouter } from 'react-router-dom';
import produce from 'immer';
import axios from 'axios';

import { Button } from 'react-bootstrap';

import AddEditItemModal from './AddEditItemModal';
import DataTable from './DataTable';

const baseURI = process.env.REACT_APP_API_URI + '/';
const itemsURI = baseURI + 'items/parris-review/';

// Data Formatting
function Image(props) {
	return <div className="text-center"><img className="table-thumbnail" src={baseURI+props.item.imageRef} alt="Item" /></div>
}

function itemMediumString(item) {
	// TODO: simplify/clarify code, & abstract into single (separate) file, for use across components
	// TODO: merge medium into mediums
	if (item.medium === '[Parris Review]' && item.mediums.length > 0) {
		const selectedMediumsCount = item.mediums.length;
		const separator = ', ';
		const terminalConjunction = '& ';
		const connectionPreposition = ' on ';
		let selectedMediumsLabel = '';

		item.mediums.forEach((currentMedium, i) => {

			// if last medium, and mediums > 1
			selectedMediumsLabel +=
				(i === selectedMediumsCount - 1  &&  selectedMediumsCount > 1)
				// add preposition + medium
				? connectionPreposition + currentMedium
				// if second to last medium, and mediums > 2
				: (i === selectedMediumsCount - 2  &&  selectedMediumsCount > 2)
				// add terminal conjunction + medium
				? terminalConjunction + currentMedium
				// if any other but last or second to last medium
				: (i < selectedMediumsCount - 2)
					// if more than 3 mediums
				? (selectedMediumsCount > 3)
					// add medium + separator
					? currentMedium + separator
					// add medium + space
					: currentMedium + ' '
				// add medium
				: currentMedium;
		});
		return selectedMediumsLabel;
	}
	else {
		return item.medium;
	}
}

function Type(props) {
	return <em>{props.item.type}<span className="text-muted"> - {itemMediumString(props.item)}</span></em>;
}

function ParrisDecision(props) {
	const decision = props.item.parrisCollectionInfo.decision;
	const buttonStyles = {
		'KEEP': 'surprise',
		'GIFT': 'success',
		'': 'secondary'
	};
	return (decision !== "")
		?	<Button className="btn-display" variant={buttonStyles[decision]} size="xs">{decision}</Button>
		:	<em className="text-muted">Decision Not Yet Made</em>;
}

/* Database View */
class ParrisReviewListView extends Component {

	constructor(props) {
		super(props);

		this.handleItemAdded = this.handleItemAdded.bind(this);
		this.handleItemRowClick = this.handleItemRowClick.bind(this);

		this.state = {
			items: []
		};
	}

	componentDidMount() {
		axios.get(itemsURI)
			.then(response => {
				this.setState({ items: response.data });
			})
			.catch(err => console.log(err));
	}

	handleItemAdded(item) {
		this.setState(produce(draftState => { draftState.items.push(item) }));
	}

	handleItemRowClick(e, itemId) {
		if (e.ctrlKey || e.metaKey) {
			// open detail page in new tab
			window.open('/detail/'+itemId, '_blank')
		}
		else {
			// open detail page in current tab
			// this.props.handleNavClick(e);				// TODO: disabled until resolve how to close overlay on detail page load
			this.props.history.push('/detail/'+itemId);		// TODO: investigate ways to pass entire item to detail page, to avoid re-loading from server (without breaking static item links)
		}
	}

	tableColumns() {
		return [
			{ title: 'RAW', data: 'raw', searchable: true, visible: false },	// for searching (TODO: implement full server-side search capability)
			{ title: '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;', data: 'image', orderable: false },
			{ title: 'Type', data: 'type' },
			{ title: 'Title', data: 'title' },
			{ title: 'Creator', data: 'creator' },
			{ title: 'Country of Origin', data: 'country' },
			{ title: 'Parris\' Decision', data: 'parrisDecision' },
			{ title: 'Recipient Name', data: 'recipientName' },
			{ title: 'Additional Details', data: 'additionalDetails' }
		];
	}

	tableData() {
		const cellData = (cellText, emptyString) => {
			return  ReactDOMServer.renderToStaticMarkup((
				(cellText)
				?	cellText
				:	<em className="text-muted">{emptyString}</em>
			));
		};
		const recipientName = (item) => {
			return  ReactDOMServer.renderToStaticMarkup((
					(item.parrisCollectionInfo.decision === 'GIFT' && (item.parrisCollectionInfo.giftInfo.firstName !== "" || item.parrisCollectionInfo.giftInfo.lastName !== ""))
					? (item.parrisCollectionInfo.giftInfo.firstName + ' ' + item.parrisCollectionInfo.giftInfo.lastName)
					: ''
			));
		};
		return this.state.items.map((currentItem, i) => {
			return {
				raw: JSON.stringify(currentItem),
				image: ReactDOMServer.renderToStaticMarkup(<Image item={currentItem} />),
				type: ReactDOMServer.renderToStaticMarkup(<Type item={currentItem} />),
				title: cellData(currentItem.title, 'No Title'),
				creator: cellData(currentItem.creators[0], 'No Creator'),
				country: cellData(currentItem.countryOfOrigin, 'No Country'),
				parrisDecision: ReactDOMServer.renderToStaticMarkup(<ParrisDecision item={currentItem} />),
				recipientName: cellData(recipientName(currentItem), ((currentItem.parrisCollectionInfo.decision === 'GIFT') ? 'No Recipient' : 'N/A')),
				additionalDetails: cellData(currentItem.parrisCollectionInfo.giftInfo.additionalDetails, ((currentItem.parrisCollectionInfo.decision === 'GIFT') ? 'No Additional Details' : 'N/A')),
				DT_RowId: currentItem._id
			};
		});
	}

	render() {
		return (
			<div className="DatabaseView">
				<div className="position-relative">
					<AddEditItemModal key={this.props.currentUser._id} mode="add" subMode="parris-review" currentUser={this.props.currentUser} onItemAdded={this.handleItemAdded} />		{/* key set to currentUser to force re-render when currentUser is updated (https://stackoverflow.com/a/48434525/2155673) */}
				</div>
				<div>
					<DataTable columns={this.tableColumns()} data={this.tableData()} onRowClick={this.handleItemRowClick} />
				</div>
			</div>
		);
	}
}

export default withRouter(ParrisReviewListView);